import { useMemo } from 'react';

import { OrganizationStorage } from 'apis/organizationStorage';
import { appActions } from 'app/app.actions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

export enum CopilotTrialStatus {
  Ineligible = 'ineligible',
  Eligible = 'eligible',
  TrialActive = 'trial-active',
  TrialExpired = 'trial-expired',
  PremiumActive = 'premium-active',
}

const trialEligibleStatuses = [
  CopilotTrialStatus.Eligible,
  CopilotTrialStatus.TrialActive,
  CopilotTrialStatus.TrialExpired,
];

export const trialLengthInDays = 30;
const dayInMs = 86400000;

const getTrialStatus = (
  flags: ReturnType<
    typeof useFeatureFlag<'agent-copilot' | 'agent-copilot-trial-eligibility'>
  >,
  copilotTrialStatus: OrganizationStorage['copilot_trial_status'],
) => {
  if (flags.agentCopilot) {
    return CopilotTrialStatus.PremiumActive;
  }

  if (!flags.agentCopilotTrialEligibility) {
    return CopilotTrialStatus.Ineligible;
  }

  if (flags.agentCopilotTrialEligibility) {
    if (isTrialStarted(copilotTrialStatus?.trialStartedAt)) {
      const trialDaysLeft = getTrialDaysLeft(
        copilotTrialStatus?.trialStartedAt,
      );
      if (trialDaysLeft && trialDaysLeft > 0) {
        return CopilotTrialStatus.TrialActive;
      }
      return CopilotTrialStatus.TrialExpired;
    }

    return CopilotTrialStatus.Eligible;
  }

  return CopilotTrialStatus.Ineligible;
};

const isTrialStarted = (trialStartedAt: undefined | string): boolean => {
  return trialStartedAt !== undefined;
};

/**
 * @param trialStartedAt
 * @returns The number of days left in the trial. If the trial has not started
 *   or is in the future, returns null.
 */
const getTrialDaysLeft = (trialStartedAt: undefined | string) => {
  if (trialStartedAt) {
    const trialStartedAtEpoch = Date.parse(trialStartedAt);

    if (isNaN(trialStartedAtEpoch) || Date.now() < trialStartedAtEpoch) {
      // Trial date is not a valid or is in the future
      return null;
    }

    const timeRemaining =
      trialLengthInDays -
      Math.floor((Date.now() - Date.parse(trialStartedAt)) / dayInMs);

    return Math.max(0, Math.min(timeRemaining, trialLengthInDays));
  }

  return null;
};

const hasCopilot = (status: ReturnType<typeof getTrialStatus>) => {
  switch (status) {
    case CopilotTrialStatus.PremiumActive:
    case CopilotTrialStatus.TrialActive:
      return true;
    default:
      return false;
  }
};

const startTrial =
  (set: ReturnType<typeof useServerStorage<'organization'>>['set']) => () => {
    return set({
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = new Date().toISOString();
      },
    });
  };

const stopTrial =
  (set: ReturnType<typeof useServerStorage<'organization'>>['set']) => () => {
    return set({
      // Set the trial to trialInLength days ago
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = new Date(
          Date.now() - trialLengthInDays * dayInMs,
        ).toISOString();
      },
    });
  };

const resetTrial =
  (
    orgSet: ReturnType<typeof useServerStorage<'organization'>>['set'],
    userSet: ReturnType<typeof useServerStorage<'user'>>['set'],
  ) =>
  () => {
    orgSet({
      copilot_trial_status: (draft) => {
        draft.trialStartedAt = undefined;
        draft.requestForContactSent = false;
      },
    });
    userSet({
      agent_copilot: (draft) => {
        draft.trial = {
          visitedTrialPage: false,
          dismissedLeftmenuCTA: false,
          dismissedIntroModal: false,
          dismissedOutroModal: false,
        };
      },
    });
  };

const setVisitedTrialPage =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      agent_copilot: (draft) => {
        draft.trial = {
          ...draft.trial,
          visitedTrialPage: true,
        };
      },
    });
  };

const setDismissLeftMenuCTA =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      agent_copilot: (draft) => {
        draft.trial = {
          ...draft.trial,
          dismissedLeftmenuCTA: true,
        };
      },
    });
  };

const setDismissIntroModal =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      agent_copilot: (draft) => {
        draft.trial = {
          ...draft.trial,
          dismissedIntroModal: true,
        };
      },
    });
  };

const setDismissOutroModal =
  (set: ReturnType<typeof useServerStorage<'user'>>['set']) => () => {
    return set({
      agent_copilot: (draft) => {
        draft.trial = {
          ...draft.trial,
          dismissedOutroModal: true,
        };
      },
    });
  };

const setRequestForContactSent =
  (set: ReturnType<typeof useServerStorage<'organization'>>['set']) => () => {
    return set({
      copilot_trial_status: (draft) => {
        draft.requestForContactSent = true;
      },
    });
  };

const openRequestFormContactForm = (dispatch: any) => async () => {
  return await dispatch(
    appActions.showModal({
      modal: {
        type: 'app:feedbackForm',
        arg: {
          team: 'conversations',
          title: 'Send request for contact',
          placeholder: `Tell us more about what caught your attention in Copilot and how many agents would use it.`,
          disableAttachments: true,
          messageRequired: false,
          feature: 'copilotTrialReq',
        },
      },
    }),
  );
};

type UseCopilotTrialReturnType = {
  status: ReturnType<typeof getTrialStatus>;
  eligibleForTrial: boolean;
  hasCopilot: ReturnType<typeof hasCopilot>;
  startTrial: ReturnType<typeof startTrial>;
  stopTrial: ReturnType<typeof stopTrial>;
  resetTrial: ReturnType<typeof resetTrial>;
  trialDaysLeft: ReturnType<typeof getTrialDaysLeft>;
  visitedTrialPage: boolean;
  setVisitedTrialPage: ReturnType<typeof setVisitedTrialPage>;
  dismissedLeftMenuCTA: boolean;
  setDismissedLeftMenuCTA: ReturnType<typeof setDismissLeftMenuCTA>;
  dismissedIntroModal: boolean;
  setDismissedIntroModal: ReturnType<typeof setDismissIntroModal>;
  dismissedOutroModal: boolean;
  setDismissedOutroModal: ReturnType<typeof setDismissOutroModal>;
  requestForContactSent: boolean;
  setRequestForContactSent: ReturnType<typeof setRequestForContactSent>;
  openRequestFormContactForm: ReturnType<typeof openRequestFormContactForm>;
};

export const useCopilotTrial = (): UseCopilotTrialReturnType => {
  const flags = useFeatureFlag([
    'agent-copilot',
    'agent-copilot-trial-eligibility',
  ]);
  const { data: orgData, set: orgSet } = useServerStorage('organization');
  const { copilot_trial_status: orgCopilotTrialStatus } = orgData;
  const dispatch = useAppDispatch();

  const { data: userData, set: userSet } = useServerStorage('user');
  const { agent_copilot } = userData;
  const userCopilotTrialStatus = agent_copilot?.trial || {};

  const trialStatus = useMemo(
    () => getTrialStatus(flags, orgCopilotTrialStatus),
    [orgCopilotTrialStatus, flags],
  );

  return useMemo(
    () => ({
      status: trialStatus,
      eligibleForTrial: trialEligibleStatuses.includes(trialStatus),
      hasCopilot: hasCopilot(trialStatus),
      startTrial: startTrial(orgSet),
      stopTrial: stopTrial(orgSet),
      resetTrial: resetTrial(orgSet, userSet),
      trialDaysLeft: getTrialDaysLeft(orgCopilotTrialStatus?.trialStartedAt),
      visitedTrialPage: userCopilotTrialStatus?.visitedTrialPage || false,
      setVisitedTrialPage: setVisitedTrialPage(userSet),
      dismissedLeftMenuCTA:
        userCopilotTrialStatus?.dismissedLeftmenuCTA || false,
      setDismissedLeftMenuCTA: setDismissLeftMenuCTA(userSet),
      dismissedIntroModal: userCopilotTrialStatus?.dismissedIntroModal || false,
      setDismissedIntroModal: setDismissIntroModal(userSet),
      dismissedOutroModal: userCopilotTrialStatus?.dismissedOutroModal || false,
      setDismissedOutroModal: setDismissOutroModal(userSet),
      requestForContactSent:
        orgCopilotTrialStatus?.requestForContactSent || false,
      setRequestForContactSent: setRequestForContactSent(orgSet),
      openRequestFormContactForm: openRequestFormContactForm(dispatch),
    }),
    [
      trialStatus,
      orgSet,
      userSet,
      orgCopilotTrialStatus?.trialStartedAt,
      orgCopilotTrialStatus?.requestForContactSent,
      userCopilotTrialStatus?.visitedTrialPage,
      userCopilotTrialStatus?.dismissedLeftmenuCTA,
      userCopilotTrialStatus?.dismissedIntroModal,
      userCopilotTrialStatus?.dismissedOutroModal,
      dispatch,
    ],
  );
};
